import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { fmt, fmtB, fmtDate, toNumber } from '@dcc-erp/shared';
import type {
  DashboardArAging,
  DashboardJournalEntry,
  DashboardTask,
  JEStatus,
  KpiDelta,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId, useDashboard } from '../lib/queries';

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

const MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
] as const;

const MONTHS_LONG = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
] as const;

type KpiKey = 'cash' | 'ar' | 'ap' | 'revenue';

// Color is semantic, not arithmetic: a rising cash/revenue balance is good
// (green), but a rising AR/AP balance is bad (red). Decoupled from the sign of
// the delta so the arrow shows direction while the color shows good/bad.
const GOOD_WHEN_UP: Record<KpiKey, boolean> = {
  cash: true,
  ar: false,
  ap: false,
  revenue: true,
};

interface KPICard {
  key: KpiKey;
  label: string;
  value: number;
  delta: KpiDelta;
}

interface PeriodOption {
  value: string; // 'YYYYMM'
  label: string;
}

function periodToYYYYMM(d: Date): string {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonths(base: Date, delta: number): Date {
  return new Date(base.getFullYear(), base.getMonth() + delta, 1);
}

function periodToTitle(period: string): string {
  const year = Number(period.slice(0, 4));
  const monthIdx = Number(period.slice(4, 6)) - 1;
  if (monthIdx < 0 || monthIdx > 11) return period;
  return `ภาพรวมการเงิน — งวดเดือน${MONTHS_LONG[monthIdx]} ${year}`;
}

function shortPeriodLabel(period: string): string {
  const year = Number(period.slice(0, 4));
  const monthIdx = Number(period.slice(4, 6)) - 1;
  if (monthIdx < 0 || monthIdx > 11) return period;
  return `${MONTHS[monthIdx]} ${year}`;
}

function StatusBadge({ status }: { status: JEStatus }) {
  if (status === 'POSTED') {
    return (
      <span className="badge positive">
        <span className="bdot" />
        Posted
      </span>
    );
  }
  if (status === 'PENDING') {
    return (
      <span className="badge warning">
        <span className="bdot" />
        รออนุมัติ
      </span>
    );
  }
  if (status === 'REVERSED') {
    return (
      <span className="badge negative">
        <span className="bdot" />
        Reversed
      </span>
    );
  }
  return (
    <span className="badge neutral">
      <span className="bdot" />
      Draft
    </span>
  );
}

// Quick-action menu options. Each navigates to the relevant route where the
// existing 'Add/Create' modal can be opened from. Order mirrors the most
// common day-to-day workflows for accounting users.
interface QuickAction {
  label: string;
  route: string;
}
const QUICK_ACTIONS: readonly QuickAction[] = [
  { label: 'ใบสำคัญทั่วไป (JV)', route: '/journal' },
  { label: 'ใบกำกับภาษีขาย (AR Invoice)', route: '/ar' },
  { label: 'ใบสำคัญจ่าย (AP Bill)', route: '/ap' },
  { label: 'ขออนุมัติจ่ายเงิน (PR)', route: '/payreq' },
  { label: 'เงินทดรองจ่าย (Advance)', route: '/advance' },
  { label: 'เงินสดย่อย (Petty Cash)', route: '/pettycash' },
];

export function DashboardPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!showActions) return;
    function onDocClick(e: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setShowActions(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [showActions]);

  // Period selector: current month + previous two. The user can also switch
  // companies via the AppShell — the query keys on companyId so data refetches.
  const periodOptions = useMemo<readonly PeriodOption[]>(() => {
    const now = new Date();
    return [0, -1, -2].map((delta) => {
      const d = shiftMonths(now, delta);
      const v = periodToYYYYMM(d);
      return { value: v, label: `งวด: ${shortPeriodLabel(v)}` };
    });
  }, []);
  const [period, setPeriod] = useState<string>(() => periodOptions[0]!.value);

  const { data, isPending, error } = useDashboard(companyId, period);

  // Bars max — fall back to 1 so we never divide by zero when there are no
  // postings yet (empty company / fresh seed).
  const maxBar = useMemo(() => {
    if (!data) return 1;
    const max = Math.max(...data.revenueByMonth, ...data.expenseByMonth);
    return max > 0 ? max : 1;
  }, [data]);

  // Real period-over-period deltas from the API (GL-coherent). Cash vs prev
  // quarter, AR/AP vs prev month, revenue YoY. `deltaPct: null` (no prior base)
  // renders "—".
  const kpis: readonly KPICard[] = data
    ? [
        {
          key: 'cash',
          label: 'เงินสด & เทียบเท่า',
          value: toNumber(data.kpi.cash),
          delta: data.kpiDeltas.cash,
        },
        {
          key: 'ar',
          label: 'ลูกหนี้คงค้าง',
          value: toNumber(data.kpi.ar),
          delta: data.kpiDeltas.ar,
        },
        {
          key: 'ap',
          label: 'เจ้าหนี้คงค้าง',
          value: toNumber(data.kpi.ap),
          delta: data.kpiDeltas.ap,
        },
        {
          key: 'revenue',
          label: 'รายได้สะสม YTD',
          value: toNumber(data.kpi.revenue),
          delta: data.kpiDeltas.revenue,
        },
      ]
    : [];

  const title = data ? periodToTitle(data.period) : periodToTitle(period);

  return (
    <div className="page">
      <PageHead
        crumbs={['ภาพรวม', 'Dashboard']}
        title={title}
        actions={
          <>
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {periodOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button className="btn">
              <Icon name="refresh" size={14} /> รีเฟรช
            </button>
            <button className="btn">
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <div ref={actionsRef} style={{ position: 'relative' }}>
              <button
                className="btn primary"
                onClick={() => setShowActions((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showActions}
              >
                <Icon name="plus" size={14} /> สร้างรายการ
                <span style={{ marginLeft: 4, fontSize: 10 }}>▾</span>
              </button>
              {showActions && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: 0,
                    minWidth: 240,
                    background: '#fff',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                    zIndex: 50,
                    padding: 4,
                  }}
                >
                  {QUICK_ACTIONS.map((a) => (
                    <button
                      key={a.route}
                      role="menuitem"
                      onClick={() => {
                        setShowActions(false);
                        void navigate({ to: a.route });
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 14,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--brand-tint-2, #f1f5f9)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        }
      />

      {error && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: 'var(--section-gap)',
            background: 'var(--negative-bg)',
            color: 'var(--negative)',
            borderRadius: 8,
            border: '1px solid var(--negative)',
          }}
        >
          โหลดข้อมูลแดชบอร์ดล้มเหลว: {error.message}
        </div>
      )}

      <div className="kpi-grid">
        {isPending || !data
          ? // Skeleton: 4 muted placeholder cards while we wait. Preserves the
            // grid layout so the page doesn't reflow when data arrives.
            Array.from({ length: 4 }).map((_, i) => (
              <div className="kpi" key={i}>
                <div className="label">
                  <span className="dot" />
                  <span className="muted">กำลังโหลด…</span>
                </div>
                <div className="value tnum muted">
                  — <span className="unit">บาท</span>
                </div>
                <div className="trend faint">—</div>
              </div>
            ))
          : kpis.map((c) => {
              const d = c.delta;
              const hasDelta = d.deltaPct !== null;
              const isGood = (d.dir === 'up') === GOOD_WHEN_UP[c.key];
              // arrow = actual direction; color class = good/bad. No delta or
              // flat → muted (faint), matching the loading skeleton.
              const trendClass = !hasDelta || d.dir === 'flat' ? 'faint' : isGood ? 'up' : 'down';
              const sign = d.deltaPct !== null && d.deltaPct > 0 ? '+' : '';
              return (
                <div className="kpi" key={c.key}>
                  <div className="label">
                    <span className="dot" />
                    {c.label}
                  </div>
                  <div className="value tnum">
                    {fmtB(c.value)} <span className="unit">บาท</span>
                  </div>
                  <div className={`trend ${trendClass}`}>
                    {hasDelta && d.dir !== 'flat' && (
                      <Icon name={d.dir === 'up' ? 'arrowUp' : 'arrowDn'} size={11} />
                    )}
                    {hasDelta ? `${sign}${d.deltaPct}%` : '—'}{' '}
                    <span className="faint" style={{ marginLeft: 4 }}>
                      {d.sub}
                    </span>
                  </div>
                </div>
              );
            })}
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <div>
              <h3>รายได้ vs ค่าใช้จ่าย — รายเดือน</h3>
              <div className="sub">หน่วย: ล้านบาท · งบรวม 12 เดือนล่าสุด</div>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: 14 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span
                  style={{ width: 9, height: 9, background: 'var(--brand)', borderRadius: 2 }}
                />
                รายได้
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    background: 'var(--brand-tint-2)',
                    borderRadius: 2,
                  }}
                />
                ค่าใช้จ่าย
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="bars">
              {MONTHS.map((m, i) => {
                const rev = data?.revenueByMonth[i] ?? 0;
                const exp = data?.expenseByMonth[i] ?? 0;
                return (
                  <div className="bcol" key={i}>
                    <div className="bstack">
                      <div
                        className="b1"
                        style={{ height: `${(rev / maxBar) * 100}%` }}
                        title={`รายได้ ${rev}M`}
                      />
                      <div
                        className="b2"
                        style={{ height: `${(exp / maxBar) * 100}%` }}
                        title={`ค่าใช้จ่าย ${exp}M`}
                      />
                    </div>
                    <div className="blabel">{m}</div>
                  </div>
                );
              })}
            </div>
            <div className="divider" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3,1fr)',
                gap: 8,
                fontSize: 14,
              }}
            >
              <div>
                <div className="faint" style={{ fontSize: 14 }}>
                  กำไรขั้นต้น
                </div>
                {/* True gross margin = (revenue − cost) / revenue (excludes opex). */}
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {data && data.kpi.grossMarginPct !== null ? `${data.kpi.grossMarginPct}%` : '—'}
                </div>
              </div>
              <div>
                <div className="faint" style={{ fontSize: 14 }}>
                  กำไรสุทธิ YTD
                </div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {data ? fmtB(toNumber(data.kpi.netIncome)) : '—'} บาท
                </div>
              </div>
              <div>
                <div className="faint" style={{ fontSize: 14 }}>
                  EBITDA Margin
                </div>
                {/* EBITDA add-backs: interest + FA/lease depreciation (tax omitted). */}
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {data && data.kpi.ebitdaMarginPct !== null ? `${data.kpi.ebitdaMarginPct}%` : '—'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <ArAgingCard aging={data?.arAging} arTotal={data ? toNumber(data.kpi.ar) : 0} />
      </div>

      <DashboardLowerGrid tasks={data?.tasks ?? []} />

      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>รายการบันทึกล่าสุด</h3>
            <div className="sub">8 รายการล่าสุดที่โพสต์ในระบบ</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm">
              <Icon name="filter" size={12} /> กรอง
            </button>
            <button className="btn sm">ดูทั้งหมด →</button>
          </div>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>เลขที่</th>
                <th>วันที่</th>
                <th>รายการ</th>
                <th className="num">เดบิตรวม</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {isPending || !data ? (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : data.recentJournals.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    ยังไม่มีรายการที่โพสต์
                  </td>
                </tr>
              ) : (
                data.recentJournals.map((j: DashboardJournalEntry) => (
                  <tr key={j.id}>
                    <td className="code">
                      <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{j.voucherNo}</a>
                    </td>
                    <td>{fmtDate(j.voucherDate)}</td>
                    <td>{j.description}</td>
                    <td className="num tnum">{fmt(toNumber(j.totalDebit))}</td>
                    <td>
                      <StatusBadge status={j.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface AgingBucket {
  label: string;
  value: number;
  color: string;
}

// AR aging stub. The Phase-4 AR subledger will replace `data.arAging`'s zeros
// with real buckets; until then we render the section with a single "no data"
// hint so the dashboard layout doesn't visually collapse.
function ArAgingCard({ aging, arTotal }: { aging: DashboardArAging | undefined; arTotal: number }) {
  const buckets: readonly AgingBucket[] = aging
    ? [
        { label: 'ยังไม่ครบกำหนด', value: toNumber(aging.notDue), color: '#117a3d' },
        { label: '1–30 วัน', value: toNumber(aging.d1_30), color: '#1a56a6' },
        { label: '31–60 วัน', value: toNumber(aging.d31_60), color: '#b54708' },
        { label: '61–90 วัน', value: toNumber(aging.d61_90), color: '#c2410c' },
        { label: '>90 วัน', value: toNumber(aging.over90), color: '#b42318' },
      ]
    : [];
  const agingTotal = buckets.reduce((a, b) => a + b.value, 0);
  const hasData = agingTotal > 0;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>ลูกหนี้คงค้าง — แยกตามอายุหนี้</h3>
          <div className="sub">รวม {fmtB(arTotal)} บาท</div>
        </div>
        <a style={{ fontSize: 14, color: 'var(--brand)', fontWeight: 500 }}>ดูทั้งหมด →</a>
      </div>
      <div className="card-body">
        {buckets.map((a) => (
          <div className="aging-row" key={a.label}>
            <div className="muted">{a.label}</div>
            <div className="aging-bar">
              <div
                className="seg"
                style={{
                  width: hasData ? `${(a.value / agingTotal) * 100}%` : '0%',
                  background: a.color,
                }}
              />
            </div>
            <div className="right tnum">
              <b>{a.value.toFixed(1)}M</b> <span className="faint">฿</span>
            </div>
          </div>
        ))}
        {!hasData && (
          <div className="muted" style={{ fontSize: 14, marginTop: 8, textAlign: 'center' }}>
            ยังไม่มีข้อมูล AR — รอ subledger ระยะถัดไป
          </div>
        )}
      </div>
    </div>
  );
}

// Lower 3-card panel. Cashflow + top-customers are Tier-2 (subledger/heuristic,
// cannot be validated on an empty company) → honest empty-states until bookings
// exist. Tasks is Tier-1 real (GL-coherent counts from the API).
function DashboardLowerGrid({ tasks }: { tasks: readonly DashboardTask[] }) {
  return (
    <div className="grid-3" style={{ marginBottom: 'var(--section-gap)' }}>
      <div className="card">
        <div className="card-head">
          <h3>กระแสเงินสด — สัปดาห์นี้</h3>
        </div>
        <div className="card-body">
          <EmptyState text="ยังไม่มีความเคลื่อนไหวของเงินสดในสัปดาห์นี้" />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>ลูกค้ารายใหญ่ — YTD</h3>
        </div>
        <div className="card-body">
          <EmptyState text="ยังไม่มีข้อมูลการขาย" />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>งานที่ต้องดำเนินการ</h3>
          {tasks.length > 0 && <span className="badge negative">{tasks.length} งาน</span>}
        </div>
        <div className="card-body" style={{ padding: tasks.length > 0 ? 0 : undefined }}>
          {tasks.length === 0 ? (
            <EmptyState text="ยังไม่มีงานค้าง" />
          ) : (
            tasks.map((t, i) => <TaskRow key={t.key} task={t} last={i === tasks.length - 1} />)
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="muted" style={{ fontSize: 14, textAlign: 'center', padding: '24px 8px' }}>
      {text}
    </div>
  );
}

// First Thai grapheme of the task title, used as the round status glyph.
function taskGlyph(title: string): string {
  return [...title][0] ?? '•';
}

function TaskRow({ task, last }: { task: DashboardTask; last: boolean }) {
  const navigate = useNavigate();
  const colorMap = {
    negative: { bg: 'var(--negative-bg)', fg: 'var(--negative)' },
    warning: { bg: 'var(--warning-bg)', fg: 'var(--warning)' },
    info: { bg: 'var(--info-bg)', fg: 'var(--info)' },
    muted: { bg: '#ececef', fg: '#52525b' },
  } as const;
  const c = colorMap[task.tone];
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: task.route })}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        cursor: 'pointer',
        padding: '10px 14px',
        borderBottom: last ? 'none' : '1px solid var(--border)',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: c.bg,
          color: c.fg,
          display: 'grid',
          placeItems: 'center',
          fontSize: 14,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {taskGlyph(task.title)}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{task.title}</div>
        <div className="faint" style={{ fontSize: 14 }}>
          {task.sub}
        </div>
      </div>
    </button>
  );
}
